import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  psGetAll,
  usePowerSyncChange,
} from '@/renderer/lib/powersync/ipcBridge';
import { useSyncStatus } from '@hooks/useSyncStatus';
import { MenuItem } from '@/renderer/types/menu';

export function useMenuItems(outletId?: string | null, searchQuery?: string) {
  const queryClient = useQueryClient();
  const { hasSynced } = useSyncStatus();
  const prevHasSynced = useRef(hasSynced);

  // Re-fetch when PowerSync syncs new menu item or department data.
  usePowerSyncChange(['menu_items', 'departments'], () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.menuItems(outletId, searchQuery),
    });
  });

  // When PowerSync completes its very first sync, invalidate so the query
  // picks up the data that was just loaded into local SQLite. Without this,
  // if the sync finishes before the component mounts, usePowerSyncChange
  // never fires and the page stays blank (staleTime is Infinity).
  useEffect(() => {
    if (!prevHasSynced.current && hasSynced && outletId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.menu.menuItems(outletId, searchQuery),
      });
    }
    prevHasSynced.current = hasSynced;
  }, [hasSynced, outletId, searchQuery, queryClient]);

  return useQuery({
    queryKey: queryKeys.menu.menuItems(outletId, searchQuery),
    queryFn: async (): Promise<MenuItem[]> => {
      if (!outletId) return [];

      const nameLike = searchQuery ? `%${searchQuery}%` : null;
      const rows = await psGetAll<any>(
        `SELECT mi.id, mi.name, mi.outlet_id, mi.category_id, mi.department_id,
                mi.base_price, mi.status, mi.display_order, mi.is_taxable,
                mi.tax_rate, mi.description, mi.image_url, mi.sku,
                mi.created_at, mi.updated_at,
                mc.name AS category_name,
                d.name  AS department_name
         FROM menu_items mi
         LEFT JOIN menu_categories mc ON mc.id = mi.category_id
         LEFT JOIN departments      d  ON d.id  = mi.department_id
         WHERE mi.outlet_id = ?
           AND mi.status = 'active'
           AND (? IS NULL OR LOWER(mi.name) LIKE LOWER(?))
         ORDER BY mi.display_order ASC, mi.name ASC`,
        [outletId, nameLike, nameLike],
      );

      return rows.map((r) => ({
        id: r.id,
        outlet_id: r.outlet_id || outletId,
        name: r.name || '',
        category_id: r.category_id ?? undefined,
        department_id: r.department_id ?? undefined,
        base_price: Number(r.base_price) || 0,
        status: r.status || 'active',
        display_order: Number(r.display_order) || 0,
        is_taxable: Boolean(Number(r.is_taxable)),
        tax_rate: Number(r.tax_rate) || 0,
        description: r.description ?? undefined,
        image_url: r.image_url ?? undefined,
        sku: r.sku ?? undefined,
        created_at: r.created_at || '',
        updated_at: r.updated_at || '',
        category_name: r.category_name ?? undefined,
        department_name: r.department_name ?? undefined,
      }));
    },
    staleTime: Infinity,
    enabled: Boolean(outletId),
  });
}
